import {
  CalendarCheckIcon,
  ChatCircleTextIcon,
  FolderPlusIcon,
  GearSixIcon,
  ListChecksIcon,
  MegaphoneIcon,
  SparkleIcon,
  WarningCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { FOLDER_COLORS, type HomeItem, type Page } from '@regia/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { errorMessage, http } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { formatDayLong, formatWhen } from '../../lib/format';
import { keys } from '../../lib/query';
import {
  ButtonLink,
  CheckCircle,
  ColorDot,
  EmptyState,
  IconButton,
  ListGroup,
  PageHeader,
  Section,
  Skeleton,
} from '../../ui';
import { PageBody } from '../common';
import { NotificationBell } from '../notifications';
import { useHome, usePages } from '../pages/api';
import { PageFolders } from '../pages/PageFolders';
import { homeSections } from '../registry';
import { AgentsStrip } from '../chat';
import { InstallBanner } from './InstallBanner';

function greeting(name?: string): string {
  const h = new Date().getHours();
  const hello = h < 13 ? 'Buongiorno' : h < 18 ? 'Buon pomeriggio' : 'Buonasera';
  return name ? `${hello}, ${name.split(' ')[0]}.` : `${hello}.`;
}

const KIND_ICON = {
  task: ListChecksIcon,
  post: MegaphoneIcon,
  reminder: CalendarCheckIcon,
  alert: WarningCircleIcon,
  insight: SparkleIcon,
} as const;

function HomeRow({ item, page }: { item: HomeItem; page?: Page }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [done, setDone] = useState(false);
  const IconCmp =
    item.severity === 'alert'
      ? WarningCircleIcon
      : item.severity === 'warn'
        ? WarningIcon
        : KIND_ICON[item.kind];
  const color = page ? FOLDER_COLORS[page.color].bg : undefined;

  async function complete(checked: boolean) {
    if (!item.completeAction || !checked) return;
    setDone(true);
    try {
      await http.post(item.completeAction);
      toast.success('Fatto');
      qc.invalidateQueries({ queryKey: keys.home });
    } catch (err) {
      setDone(false);
      toast.error(errorMessage(err));
    }
  }

  return (
    <li className="flex min-h-14 items-center gap-3 px-4 py-2">
      {item.completeAction ? (
        <CheckCircle
          checked={done}
          onChange={complete}
          label={`Segna come fatto: ${item.title}`}
          color={color}
        />
      ) : (
        <IconCmp
          size={24}
          className={
            item.severity === 'alert'
              ? 'text-alert'
              : item.severity === 'warn'
                ? 'text-warn'
                : 'text-ink-2'
          }
          aria-hidden
        />
      )}
      <button
        type="button"
        onClick={() => navigate(item.href)}
        className="flex min-w-0 flex-1 flex-col py-1 text-left"
      >
        <span
          className={`truncate text-[0.9375rem] font-medium ${done ? 'text-ink-3 line-through' : 'text-ink'}`}
        >
          {item.title}
        </span>
        <span className="flex min-w-0 items-center text-sm text-ink-2">
          {page ? (
            <>
              <ColorDot color={FOLDER_COLORS[page.color].bg} size={8} className="mr-1.5" />
              <span className="truncate">{page.name}</span>
            </>
          ) : null}
          {item.dueAt ? (
            <span className="shrink-0">
              {page ? ', ' : ''}
              {formatWhen(item.dueAt)}
            </span>
          ) : null}
          {item.detail && !item.dueAt ? (
            <span className="truncate">
              {page ? ', ' : ''}
              {item.detail}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

export function TodayPage() {
  const navigate = useNavigate();
  const home = useHome();
  const pages = usePages();
  const me = useMe();
  const byId = new Map((pages.data ?? []).map((p) => [p.id, p]));
  const items = home.data?.items ?? [];
  const alerts = items.filter((i) => i.kind === 'alert');
  const todo = items.filter((i) => i.kind !== 'alert');

  return (
    <PageBody>
      <PageHeader
        title={home.data ? formatDayLong(home.data.date) : formatDayLong(new Date())}
        subtitle={greeting(me?.name)}
        actions={
          <div className="flex items-center gap-1">
            <NotificationBell />
            {/* Sul telefono le Impostazioni non sono nella barra in basso: si aprono da qui. */}
            <IconButton icon={GearSixIcon} label="Impostazioni" className="lg:hidden" onClick={() => navigate('/impostazioni')} />
          </div>
        }
      />
      <AgentsStrip />
      <InstallBanner />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-x-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,27rem)]">
        <div>
          <Section title="Da fare oggi">
            {home.isLoading ? (
              <Skeleton className="h-32 w-full rounded-[var(--radius-sheet)]" />
            ) : todo.length === 0 ? (
              <div className="flex items-center gap-3 rounded-[var(--radius-sheet)] bg-sheet px-4 py-5">
                <ChatCircleTextIcon size={24} className="text-ink-3" aria-hidden />
                <p className="text-[0.9375rem] text-ink-2">
                  Niente in scadenza oggi. Buon momento per preparare i prossimi post.
                </p>
              </div>
            ) : (
              <ListGroup>
                {todo.map((item) => (
                  <HomeRow
                    key={item.id}
                    item={item}
                    page={item.pageId ? byId.get(item.pageId) : undefined}
                  />
                ))}
              </ListGroup>
            )}
          </Section>
          {alerts.length > 0 ? (
            <Section title="Avvisi">
              <ListGroup>
                {alerts.map((item) => (
                  <HomeRow
                    key={item.id}
                    item={item}
                    page={item.pageId ? byId.get(item.pageId) : undefined}
                  />
                ))}
              </ListGroup>
            </Section>
          ) : null}
          {[...homeSections]
            .sort((a, b) => a.order - b.order)
            .map((s) => (
              <s.element key={s.id} />
            ))}
        </div>
        <div>
          <Section
            title="Schede"
            action={
              pages.data && pages.data.length > 0 ? (
                <Link to="/schede" className="text-sm font-semibold text-action hover:underline">
                  Tutte
                </Link>
              ) : null
            }
          >
            {pages.isLoading ? (
              <Skeleton className="h-40 w-full rounded-[18px]" />
            ) : (pages.data ?? []).length === 0 ? (
              <EmptyState
                icon={FolderPlusIcon}
                title="Crea la prima scheda"
                message="Una cartellina per ogni pagina o cliente."
                action={
                  <ButtonLink to="/schede?nuova=1" icon={FolderPlusIcon}>
                    Nuova scheda
                  </ButtonLink>
                }
              />
            ) : (
              <PageFolders pages={pages.data ?? []} badges={home.data?.badges} />
            )}
          </Section>
        </div>
      </div>
    </PageBody>
  );
}
